import { Component } from '@angular/core';
import { OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { MetadataService } from '../../services/metadata.service';
import { UserService } from '../../services/user.service';
import { ProductService } from '../../services/product.service';
import { SearchService } from '../../search/services/search.service';
import { ViewerService } from '../../services/viewer.service';
import { CopyLinkComponent } from '../../components/copy-link/copy-link.component';
import { RoutingService} from '../../services/routing.service';
import dayjs from 'dayjs';

/**
 * Component for displaying the metadata of a given product.
 */
@Component({
	selector: 'metadata-view',
	templateUrl: './metadata-view.component.html',
	styleUrl: './metadata-view.component.css'
})
export class MetadataViewComponent implements OnInit, OnChanges {
	@Input() productId: any;

	LOCALIZATION: any;
	Object = Object;

	recordTitle: string = '';
	record: { [key: string]: any } = {};
	views: any;
	searchableMetadataFields: any[] = [];

	activeTab: string = 'standard';
	tabs = [{
		id: 'dc',
		mode: 'dc',
		title: '',
		defaultTitle: 'Dublin Core',
		canaccess: true,
		metadata: ''
	},
	{
		id: 'marc21xml',
		mode: 'marc21',
		title: '',
		defaultTitle: 'Marc21',
		canaccess: true,
		metadata: ''
	},
	{
		id: 'rawMetadata',
		mode: 'm',
		title: '',
		defaultTitle: 'Raw',
		canaccess: true,
		metadata: ''
	}];

	dateFormatDisplay: string = 'MM/DD/YY';
	pluralMapping = {
		'=1': '1 view ',
		'other': '# views '
	};

	constructor(
		private configService: ConfigService,
		private alertsService: AlertsService,
		private metadataService: MetadataService,
		private userService: UserService,
		private searchService: SearchService,
		private viewerService: ViewerService,
		private productService: ProductService,
		private routingService: RoutingService,
		private ngbModal: NgbModal
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.searchableMetadataFields = this.configService.getConfig().search.searchableMetadataFields;

		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find(function(x: any) { return x.name == "USER_INTERFACE_DATE_TYPE"; });
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';
	}

	ngOnInit(): void {
		this.loadRecord();
		this.initTabs();
		this.loadDocumentViews();
	}

	/**
	 * Reinitialize this component without destroying the DOM.
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['productId']) {
			this.loadRecord();
			this.initTabs();
			this.loadDocumentViews();
		}
	}

	/**
	 * Loads the standard KV product metadata.
	 */
	loadRecord(): void {
		this.searchService.getRecord(this.productId).then(
			(response: any) => {
				let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "IS_HIGHLIGHT_VIEW_FIELD" });
				if (property.value === "yes") {
					this.viewerService.getHighlightedMetadataRecord(this.productId).then(
						(highlights: any) => {
							this.processMetadata(response, highlights);
						},
						(err: any) => {
							console.error("Failed to load product metadata", err);
							this.alertsService.error({
								message: "Failed to retrieve metadata for this document."
							});
						}
					);
				} else {
					this.processMetadata(response);
				}
			},
			(err: any) => {
				console.error("Failed to load product metadata", err);
				this.alertsService.error({
					message: "Failed to retrieve metadata for this document."
				});
			}
		);
	}

	private processMetadata(result: any, highlights?: any) {
		let hl: { [key: string]: any } = {};
		if (highlights && highlights["aw:record"]["print"]) {
			hl = highlights["aw:record"]["print"];
		}

		let productHash = result["csw:GetRecordByIdResponse"].iStoreRecord;
		let productInfo: { [key: string]: any } = {};
		let validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => { return x.isAvailable && x.isMetadata });

		for (let p = 0; p < validFields.length; p++) {
			let field = validFields[p];

			if (!productHash[field.name]) {
				continue;
			}

			productInfo[field.displayName] = {
				label: field.displayName,
				value: hl[field.name] ? hl[field.name] : productHash[field.name],
				maxLength: (field.isLink ? -1 : 220),
				links: null,
				name: field.name
			};

			productInfo[field.displayName].cleanedValue = this.metadataService.cleanValue(productInfo[field.displayName].value);
			productInfo[field.displayName].isValidCollection =
				this.metadataService.isValidFieldCollectionValue(productInfo[field.displayName].name, productInfo[field.displayName].cleanedValue);

			if (field.type == "DATE") {
				productInfo[field.displayName].value = dayjs(productInfo[field.displayName].value).format(this.dateFormatDisplay);
			}

			if (field.name == "requires") {
				productInfo[field.displayName].warning = true;
			}

			if (field.isLink) {
				productInfo[field.displayName].links = this.metadataService.linksFromString(productHash[field.name],field.name);
			} else {
				if (productInfo[field.displayName].value) {
					productInfo[field.displayName].value = productInfo[field.displayName].value.replace(/; /g, ';').replace(/;/g, '; ');
				}
			}
		}

		this.record = productInfo;
	}

	private initTabs(): void {
		const metadataTabs = this.configService.getConfig().search?.metadataTabs || [];
		this.tabs.forEach((tab: any) => {
			let configtab = metadataTabs.find((config_tab: any) => config_tab.id === tab.id);
			tab.canaccess = configtab ? !configtab.hide : false;

			tab.title = this.LOCALIZATION[tab.id] || tab.defaultTitle;

			this.loadMetadataView(tab);
		});
	}

	private loadMetadataView(tab: any): void {
		this.productService.getMetadataAsFormat(this.productId, tab.mode).then(
			(response: any) => {
				tab.metadata = this.formatXml(response);
			},
			(err: any) => {
				console.error(`Failed to load product metadata format (${tab.id}).`, err);
			}
		);
	}

	private formatXml(xml: string): string {
		if (!xml) return xml;

		var formatted = '', indent = '';
		var tab = '\t';
		xml.split(/>\s*</).forEach(function(node) {
			if (node.match(/^\/\w/)) indent = indent.substring(tab.length);
			formatted += indent + '<' + node + '>\r\n';
			if (node.match(/^<?\w[^>]*[^\/]$/)) indent += tab;
		});
		return formatted.substring(1, formatted.length - 3);
	}

	private loadDocumentViews() {
		this.viewerService.getDocumentViewsInfo(this.productId).then(
			(response: any) => {
				this.views = response;
				this.views.loadDateDisplay = dayjs(this.views.loadDate).format(this.dateFormatDisplay);
			},
			(err: any) => {
				console.error("Failed to load product views", err);
			}
		);
	}

	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}

	/**
	 * Executes a search using the field and value.
	 */
	resultFieldSearch(field: string, value: string): void {
		console.log(`Searching for: ${field}, with value: ${value}`);
		let payload = {
			field: field,
			value: value,
			source: 'metadata-view'
		};
		this.searchService.broadcastSearchResultFieldEvent(payload);
	}
	
	handleBrowsepathLink(link: string): void {
		let payload = {
			link: link,
			source: 'metadata-view'
		};
		this.searchService.broadcastBrowseLinkEvent(payload);		
	}
	
	private convertBrowseString(input: string): string {
	  return input
	    .split('>>')                                // Split by '>>'
	    .map(part => {
	      const [key, value] = part.split(':');     // Split into key and value
	      return `${key}=[${value}]`;               // Format as key=[value]
	    })
	    .join('~');                                 // Join with '~'
	}
}
